/**
 * SubscriptionButton Component
 *
 * Contextual subscription button that automatically shows the right label
 * and action based on user's current subscription state
 *
 * Usage:
 * <SubscriptionButton />
 * <SubscriptionButton size="lg" className="w-full" />
 */

import { Button } from '@/shared/components/ui/button';
import { useSubscriptionButton } from '@/shared/hooks/useSubscriptionButton';
import { Sparkles, Settings, Play, RefreshCw, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SubscriptionButtonProps {
  /**
   * Button size
   */
  size?: 'default' | 'sm' | 'lg' | 'icon';

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Show icon before label (default: true)
   */
  showIcon?: boolean;

  /**
   * Override the default action (if you want custom behavior)
   */
  onClick?: () => void;

  /**
   * Force a specific label (overrides auto-detection)
   */
  label?: string;

  /**
   * Force a specific variant
   */
  variant?: 'default' | 'outline' | 'destructive' | 'secondary' | 'ghost';
}

const iconMap = {
  'sparkles': Sparkles,
  'settings': Settings,
  'play': Play,
  'refresh-cw': RefreshCw,
  'credit-card': CreditCard,
};

export function SubscriptionButton({
  size = 'default',
  className,
  showIcon = true,
  onClick,
  label: customLabel,
  variant: customVariant,
}: SubscriptionButtonProps) {
  const config = useSubscriptionButton();

  const Icon = config.icon ? iconMap[config.icon as keyof typeof iconMap] : null;

  return (
    <Button
      size={size}
      variant={customVariant || config.variant}
      className={cn(className)}
      onClick={onClick || config.action}
      disabled={config.disabled}
      title={config.description}
    >
      {showIcon && Icon && <Icon className="w-4 h-4 mr-2" />}
      {customLabel || config.label}
    </Button>
  );
}

/**
 * Compact version - just the label as a link
 */
export function SubscriptionLink({
  className,
  onClick,
}: {
  className?: string;
  onClick?: () => void;
}) {
  const config = useSubscriptionButton();

  return (
    <button
      className={cn('text-sm text-primary hover:underline', className)}
      onClick={onClick || config.action}
      title={config.description}
    >
      {config.label}
    </button>
  );
}
